import { useEffect } from 'react';
import { Text, Spinner, makeStyles, tokens } from '@fluentui/react-components';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router';
import { fetchKpis, fetchTasks, updateTaskStatus } from '../api/dashboard.api';
import { useEntityNames } from '../hooks/useEntityNames';
import { fetchRecentActivities } from '../api/lotes.api';
import { KpiCard } from '../components/shared/KpiCard';
import { ActivityFeed } from '../components/shared/ActivityFeed';
import { hasLocalTourSeen, markLocalTourSeen } from '../tour/tourSeenStorage';
import AdoptionCounter from '../components/AdoptionCounter';
import { useToast } from '../hooks/useToast';
import { useT } from '../i18n';
import { usePageTitle } from '../hooks/usePageTitle';
import { useLocaleSettings } from '../hooks/useLocaleSettings';
import { useProductTour } from '../tour/ProductTour';
import { useAuthStore } from '../store/auth.store';
import { apiClient } from '../api/client';
import { useRanchoStore } from '../store/rancho.store';
import type { Task, TaskStatus } from '../types/api.types';
import { PageTitle } from '../components/shared/PageTitle';

const useStyles = makeStyles({
  root:    { display: 'flex', flexDirection: 'column', gap: '24px' },
  kpiRow:  { display: 'flex', gap: '16px', flexWrap: 'wrap' },
  grid:    {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
    gap: '24px',
    '@media (max-width: 768px)': { gridTemplateColumns: '1fr' },
  },
  section: { display: 'flex', flexDirection: 'column', gap: '12px' },
  kanban:  { display: 'flex', gap: '16px' },
  col: {
    flex: 1,
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
    padding: '12px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  colTitle: { fontWeight: '600', fontSize: '13px', marginBottom: '4px' },
  taskCard: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusSmall,
    padding: '8px 12px',
    fontSize: '13px',
    cursor: 'pointer',
    ':hover': { backgroundColor: tokens.colorNeutralBackground1Hover },
  },
});

const STATUSES: TaskStatus[] = ['PENDING', 'IN_PROGRESS', 'COMPLETED'];

const nextStatus = (current: TaskStatus): TaskStatus => {
  const seq: TaskStatus[] = ['PENDING', 'IN_PROGRESS', 'COMPLETED'];
  return seq[Math.min(seq.indexOf(current) + 1, seq.length - 1)];
};

export function DashboardPage() {
  const s    = useStyles();
  const t    = useT();
  usePageTitle(t.pageTitle.dashboard);
  const qc   = useQueryClient();
  const { tenantId } = useParams<{ tenantId: string }>();
  const toast = useToast();
  const { formatCurrency } = useLocaleSettings();
  const { loteName } = useEntityNames();
  const today = new Date().toISOString().slice(0, 10);
  const { start: startProductTour } = useProductTour();
  const user = useAuthStore(state => state.user);
  const token = useAuthStore(state => state.token);
  const setSession = useAuthStore(state => state.setSession);

  // Task 20 (self-registration-onboarding): dispara el product tour la primera vez
  // que el usuario ve el dashboard, y marca el flag vía el endpoint dedicado
  // PATCH /users/me/onboarding (merge parcial — no reusa el PUT de preferences).
  useEffect(() => {
    if (!tenantId || !user) return;
    if (user.preferences?.hasSeenProductTour) return;
    if (hasLocalTourSeen(user.phoneNumber)) return;

    startProductTour();
    apiClient
      .patch(`/api/users/me/onboarding`, { hasSeenProductTour: true })
      .then(() => {
        // Fix (code review, task 20): sin esto, el store local nunca se
        // actualiza tras el PATCH exitoso — al remontar el componente
        // (navegar fuera y volver) `hasSeenProductTour` seguía viéndose
        // falso y el tour + PATCH se repetían en cada visita de la sesión.
        if (token) {
          setSession(token, {
            ...user,
            preferences: {
              language: user.preferences?.language ?? 'es',
              theme: user.preferences?.theme ?? 'light',
              ...user.preferences,
              hasSeenProductTour: true,
            },
          });
        }
      })
      // Si el PATCH falla (red/500) no repetir el tour en este navegador.
      .catch(() => { markLocalTourSeen(user.phoneNumber); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, user?.preferences?.hasSeenProductTour]);

  const activeRanchoId = useRanchoStore(s => s.activeRanchoId);
  const { data: kpis, isError: kpisError } = useQuery({ queryKey: ['kpis', activeRanchoId], queryFn: () => fetchKpis(activeRanchoId) });

  const { data: tasks = [], isLoading: loadingTasks } = useQuery({
    queryKey: ['tasks', today, activeRanchoId],
    queryFn:  () => fetchTasks(today, activeRanchoId),
  });

  const { data: recentActivities = [], isLoading: loadingActs } = useQuery({
    queryKey: ['recentActivities', activeRanchoId],
    queryFn:  () => fetchRecentActivities(10, activeRanchoId),
  });

  const moveMut = useMutation({
    mutationFn: ({ id, loteId, status }: { id: string; loteId: string; status: TaskStatus }) =>
      updateTaskStatus(id, loteId, status),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['tasks'] }); toast.success(t.common.save); },
    onError:   () => toast.error(t.common.noData),
  });

  return (
    <div className={s.root}>
      <PageTitle>{t.dashboard.title}</PageTitle>

      {tenantId && <AdoptionCounter tenantId={tenantId} />}

      {/* KPIs */}
      <div className={s.kpiRow}>
        {kpis ? (
          <>
            <KpiCard label={t.dashboard.kpiCostToday}    value={formatCurrency(kpis.costToday)} sub={kpis.date} />
            <KpiCard label={t.dashboard.kpiActivities}   value={kpis.activityCount}  sub={t.dashboard.kpiActivitiesSub} />
            <KpiCard label={t.dashboard.kpiPendingTasks} value={kpis.pendingTasks}   accent={kpis.pendingTasks > 5} />
            <KpiCard label={t.dashboard.kpiOverdueTasks} value={kpis.overdueTasks}   accent={kpis.overdueTasks > 0} />
            <KpiCard label={t.dashboard.kpiActiveLots}   value={kpis.loteCount} />
          </>
        ) : kpisError ? (
          <Text style={{ color: tokens.colorStatusDangerForeground1 }}>{t.common.noData}</Text>
        ) : (
          <Spinner size="small" label={t.dashboard.loadingKpis} />
        )}
      </div>

      {/* Kanban + Activity */}
      <div className={s.grid}>
        <div className={s.section}>
          <Text weight="semibold">{t.dashboard.kanbanTitle}</Text>
          {loadingTasks ? (
            <Spinner size="small" />
          ) : (
            <div className={s.kanban}>
              {STATUSES.map((st) => (
                <div key={st} className={s.col}>
                  <div className={s.colTitle}>{t.taskStatus[st]}</div>
                  {(tasks as Task[]).filter((tk) => tk.status === st).map((tk) => (
                    <div
                      key={tk.taskId}
                      className={s.taskCard}
                      onClick={() =>
                        st !== 'COMPLETED' &&
                        moveMut.mutate({ id: tk.taskId, loteId: tk.loteId, status: nextStatus(st) })
                      }
                      title={st !== 'COMPLETED' ? `${t.dashboard.moveTo} ${t.taskStatus[nextStatus(st)]}` : undefined}
                    >
                      {tk.title}
                      {tk.loteId && (
                        <div style={{ fontSize: '11px', color: tokens.colorNeutralForeground3 }}>
                          {loteName(tk.loteId)}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className={s.section}>
          <Text weight="semibold">{t.dashboard.activityTitle}</Text>
          <ActivityFeed activities={recentActivities} isLoading={loadingActs} />
        </div>
      </div>
    </div>
  );
}
